// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Button, Group, Loader, Modal, Paper, ScrollArea, Stack, Text, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import type { Patient } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { IconCalendarRepeat, IconVideo } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { AppointmentDetails } from '../../hooks/useAppointmentDetails';
import { cancelPatientAppointment } from '../../services/appointment-lifecycle-service';
import { formatAppointmentDate } from '../../utils/appointment-formatting';
import { ensurePatientTimezone } from '../../utils/patient-timezone';
import { AppointmentCancellationModal } from '../appointments/AppointmentCancellationModal';
import { AppointmentJoinButton } from '../appointments/AppointmentJoinButton';
import { AppointmentTimeWithTimezone } from '../appointments/AppointmentTimeWithTimezone';
import classes from './AppointmentsSection.module.css';
import sectionClasses from './dashboard-section.module.css';

const scrollAreaStyles = {
  thumb: { backgroundColor: 'var(--mantine-color-polaris-5)' },
};

interface AppointmentsSectionProps {
  appointments: AppointmentDetails[];
  loading: boolean;
  onRefresh: () => void;
}

/**
 * Horizontal row of upcoming-appointment cards plus a "Schedule" CTA. Wraps
 * the existing patient-side appointment data; tightly mirrors the visual of
 * the provider Dashboard's task cards row but with patient-appropriate
 * actions (Join / Reschedule / Cancel).
 */
export function AppointmentsSection({ appointments, loading, onRefresh }: AppointmentsSectionProps): JSX.Element {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient | undefined;
  const [cancelModalOpened, cancelModalHandlers] = useDisclosure(false);
  const [cancelTarget, setCancelTarget] = useState<AppointmentDetails | undefined>();
  const [cancelling, setCancelling] = useState(false);
  const [confirmationOpened, confirmationHandlers] = useDisclosure(false);

  const handleSchedule = (): void => {
    navigate('/appointments/schedule');
  };

  const openCancel = (details: AppointmentDetails): void => {
    setCancelTarget(details);
    cancelModalHandlers.open();
  };

  const handleConfirmCancel = async (): Promise<void> => {
    if (!cancelTarget?.appointment.id) {
      return;
    }
    setCancelling(true);
    try {
      if (profile) {
        await ensurePatientTimezone(medplum, profile);
      }
      await cancelPatientAppointment(medplum, cancelTarget.appointment.id);
      showNotification({
        color: 'green',
        title: 'Appointment cancelled',
        message: 'Your appointment has been cancelled successfully.',
      });
      cancelModalHandlers.close();
      confirmationHandlers.close();
      onRefresh();
    } catch (err) {
      showNotification({
        color: 'red',
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to cancel appointment',
      });
    } finally {
      setCancelling(false);
    }
  };

  let body: JSX.Element;
  if (loading) {
    body = (
      <Group justify="center" py="md">
        <Loader size="sm" />
      </Group>
    );
  } else if (appointments.length === 0) {
    body = (
      <Text c="dimmed" py="md">
        You do not have any scheduled appointments
      </Text>
    );
  } else {
    body = (
      <ScrollArea scrollbarSize={6} type="auto" styles={scrollAreaStyles}>
        <Group gap="md" align="stretch" wrap="nowrap" pb="xl">
          {appointments.map((details) => (
            <Paper key={details.appointment.id} withBorder radius="md" p="md" bg="white" className={classes.card}>
              <Stack gap={6}>
                <Text fw={700}>{details.appointmentType}</Text>
                <Text size="sm" c="dimmed">
                  {formatAppointmentDate(details.appointment.start)}
                </Text>
                <AppointmentTimeWithTimezone
                  start={details.appointment.start}
                  end={details.appointment.end}
                  textProps={{ size: 'sm', c: 'dimmed' }}
                />
                <Text size="sm" c="dimmed">
                  with <Anchor size="sm">{details.providerName}</Anchor>
                </Text>
                <Group gap="xs" mt={4}>
                  <AppointmentJoinButton joinUrl={details.joinUrl} withIcon={<IconVideo size={14} />} />
                  <Button
                    size="xs"
                    variant="outline"
                    leftSection={<IconCalendarRepeat size={14} />}
                    onClick={() => navigate(`/appointments/reschedule/${details.appointment.id}`)}
                  >
                    Reschedule
                  </Button>
                  <Button size="xs" variant="outline" color="red" onClick={() => openCancel(details)}>
                    Cancel
                  </Button>
                </Group>
              </Stack>
            </Paper>
          ))}
        </Group>
      </ScrollArea>
    );
  }

  return (
    <>
      <Paper shadow="sm" radius="md" p="lg" className={sectionClasses.decorated}>
        <Group justify="space-between" mb="md" wrap="wrap">
          <Title order={3}>Appointments</Title>
          <Button onClick={handleSchedule}>Schedule a New Appointment</Button>
        </Group>
        {body}
      </Paper>

      <Modal
        opened={cancelModalOpened}
        onClose={cancelModalHandlers.close}
        overlayProps={{ opacity: confirmationOpened ? 0 : 0.55 }}
        centered
        withCloseButton={false}
        padding="xl"
        radius="lg"
        size="lg"
      >
        {cancelTarget && (
          <Stack gap="lg" align="center">
            <Title order={2}>Reschedule or Cancel</Title>
            <Stack gap={4} align="center">
              <Text fw={600} size="md">
                {cancelTarget.appointmentType}
              </Text>
              <Text fw={600} size="lg">
                {formatAppointmentDate(cancelTarget.appointment.start)}
              </Text>
              <AppointmentTimeWithTimezone
                start={cancelTarget.appointment.start}
                end={cancelTarget.appointment.end}
                groupProps={{ justify: 'center' }}
                textProps={{ fw: 600, size: 'lg' }}
              />
            </Stack>
            <Text size="sm" c="dimmed" ta="center">
              Would you like to reschedule or cancel this appointment?
            </Text>
            <Group justify="center" w="100%">
              <Button
                variant="outline"
                onClick={() => {
                  cancelModalHandlers.close();
                  navigate(`/appointments/reschedule/${cancelTarget.appointment.id}`);
                }}
              >
                Reschedule
              </Button>
              <Button color="red" onClick={confirmationHandlers.open}>
                Cancel appointment
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
      <AppointmentCancellationModal
        opened={confirmationOpened}
        onClose={confirmationHandlers.close}
        onConfirm={handleConfirmCancel}
        loading={cancelling}
      />
    </>
  );
}
